import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';
import { useNavigate } from 'react-router-dom';

const TYPE_ICON = {
  like:    '❤️',
  follow:  '👤',
  comment: '💬',
  mention: '@',
  default: '🔔',
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);

    // Try common field names used for receiver
    Promise.all([
      getDocs(query(collection(db, 'notifications'), where('receiverId', '==', user.uid), orderBy('createdAt', 'desc'), limit(50))),
      getDocs(query(collection(db, 'notifications'), where('userId',     '==', user.uid), orderBy('createdAt', 'desc'), limit(50))),
    ]).then(([snapA, snapB]) => {
      const seen = new Set();
      const all  = [...snapA.docs, ...snapB.docs]
        .filter(d => { if (seen.has(d.id)) return false; seen.add(d.id); return true; })
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setNotifs(all);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const markRead = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch {}
  };

  const formatTime = (ts) => {
    if (!ts?.seconds) return '';
    const d = new Date(ts.seconds * 1000);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60)   return 'À l\'instant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400)return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (!user) return (
    <div style={s.empty}>
      <span style={{ fontSize: 40 }}>🔔</span>
      <p>Connectez-vous pour voir vos notifications</p>
      <button style={s.btn} onClick={() => navigate('/login')}>Se connecter</button>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <span style={s.headerTitle}>🔔 Notifications</span>
        {notifs.some(n => !n.read) && (
          <span style={s.unreadBadge}>{notifs.filter(n => !n.read).length}</span>
        )}
      </div>

      {loading ? (
        <div style={s.list}>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} style={s.skel} />)}
        </div>
      ) : notifs.length === 0 ? (
        <div style={s.empty}>
          <span style={{ fontSize: 48 }}>🔔</span>
          <p style={s.emptyText}>Aucune notification pour l'instant</p>
        </div>
      ) : (
        <div style={s.list}>
          {notifs.map(n => (
            <div
              key={n.id}
              style={{ ...s.item, ...(n.read ? {} : s.itemUnread) }}
              onClick={() => { if (!n.read) markRead(n.id); }}
            >
              <div style={s.iconBox}>
                <span style={{ fontSize: 22 }}>{TYPE_ICON[n.type] || TYPE_ICON.default}</span>
              </div>
              <div style={s.content}>
                <p style={s.msg}>{n.message || n.body || 'Nouvelle notification'}</p>
                {n.senderUsername && (
                  <p style={s.sender}>@{n.senderUsername}</p>
                )}
                <p style={s.time}>{formatTime(n.createdAt)}</p>
              </div>
              {!n.read && <div style={s.dot} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  page       : { background: '#f7f8fa', minHeight: '100vh', paddingBottom: 80 },
  header     : { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 12px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 },
  headerTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a1a', flex: 1 },
  unreadBadge: { background: '#e74c3c', color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '2px 8px' },
  list       : { display: 'flex', flexDirection: 'column' },
  item       : { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: '#fff', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  itemUnread : { background: '#f0f4ff' },
  iconBox    : { width: 44, height: 44, borderRadius: 22, background: '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  content    : { flex: 1 },
  msg        : { fontSize: 14, color: '#1a1a1a', margin: '0 0 3px', lineHeight: 1.4 },
  sender     : { fontSize: 12, color: '#4361ee', fontWeight: 600, margin: '0 0 3px' },
  time       : { fontSize: 11, color: '#aaa', margin: 0 },
  dot        : { width: 8, height: 8, borderRadius: 4, background: '#4361ee', flexShrink: 0, marginTop: 6 },
  skel       : { height: 72, background: 'linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', margin: '1px 0' },
  empty      : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 80, color: '#aaa' },
  emptyText  : { fontSize: 14, color: '#aaa', margin: 0 },
  btn        : { background: '#4361ee', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 24px', fontSize: 14, cursor: 'pointer' },
};
